import PropTypes from 'prop-types'
import React, {forwardRef, useImperativeHandle, useRef, useMemo, useEffect} from 'react'
import {useChakraContext, Box} from '@chakra-ui/react3'
import useVideoAutoplay from '../../../hooks/useVideoAutoplay'
import {hasValidVideoSource} from '../../../utils/utils'
import useOcapiSite from '../../../../hooks/useOcapiSite'
import {getSharedLibraryAssetUrl} from '../../../../utils/url'
import Image from '../image'
import {useNonce} from '../../../../hooks'

const IS_TEST = process.env.NODE_ENV === 'test'

// DO NOT USE MODERN JS INSIDE OR REFERENCE ANYTHING OUTSIDE OF THIS FUNCTION
// This function is stringified and embedded in a script tag for SSR.
// Modern JS features may not be supported in all browsers, and external references
// will be undefined in the inline script context.
function preHydrationPosterRemover() {
    const script = document.currentScript
    const container = script.previousElementSibling
    if (!container) return

    const video = container.querySelector('video')
    const poster = container.querySelector('[data-video-poster]')
    if (!video || !poster) return

    const hidePoster = function () {
        poster.style.display = 'none'
    }

    video.addEventListener('playing', hidePoster)

    // store reference for cleanup
    container._posterRemover = {video: video, poster: poster, handler: hidePoster}
}

// Common video MIME types
const VIDEO_EXTENSIONS = {
    mp4: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
    ogv: 'video/ogg',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    wmv: 'video/x-ms-wmv',
    flv: 'video/x-flv',
    m4v: 'video/mp4',
    '3gp': 'video/3gpp',
    '3g2': 'video/3gpp2'
}

// Video format compression efficiency priority (lower = better compression)
const COMPRESSION_PRIORITY = {
    'video/av1': 1, // AV1 - best compression
    'video/webm': 2, // WebM/VP9 - very good compression
    'video/hevc': 3, // H.265/HEVC - good compression
    'video/mp4': 4, // H.264/MP4 - standard compression
    'video/ogg': 5, // Ogg/Theora - older format
    'video/quicktime': 6, // QuickTime/MOV - less efficient
    'video/x-msvideo': 7, // AVI - older format
    'video/x-ms-wmv': 8, // WMV - older format
    'video/x-flv': 9, // FLV - older format
    'video/3gpp': 10, // 3GP - mobile legacy
    'video/3gpp2': 11 // 3G2 - mobile legacy
}

/**
 * Detect video type from URL extension
 * @param {string} src - Video source URL
 * @returns {string|null} MIME type or null if not detected
 */
const detectVideoType = (src) => {
    if (!src) return null
    const extension = src.split('.').pop()?.split('?')[0]?.toLowerCase()
    return VIDEO_EXTENSIONS[extension] || null
}

/**
 * Get compression priority for a video type
 * @param {string|null|undefined} type - Video MIME type
 * @returns {number} Priority value (lower = better compression, 999 = unknown)
 */
const getCompressionPriority = (type) => {
    if (!type) return 999 // Unknown types go last
    return COMPRESSION_PRIORITY[type] || 999
}

/**
 * DirectVideo component for rendering native HTML5 video
 * Parent component controls play/pause via ref
 * Automatically resolves relative video URLs against sharedLibraryStatic from OCAPI.
 * Filters out relative URLs if sharedLibraryStatic is not available.
 * Returns null if no valid sources remain after filtering.
 * @component
 * @param {Object} props - Component properties
 * @param {Array} props.sources - Array of video sources {src, type?, breakpoint?}. src can be relative (resolved against sharedLibraryStatic) or absolute.
 * @param {Object} [props.poster] - Poster image object {src, alt?, title?}
 * @param {boolean} [props.autoplay=false] - Whether to autoplay the video (will be muted)
 * @param {boolean} [props.loop=false] - Whether the video should be in a loop or not
 * @param {boolean} [props.videoControls=false] - Whether to show native video controls
 * @param {('eager'|'lazy')} [props.loading='lazy'] - Loading strategy: 'eager' sets preload='auto', 'lazy' sets preload='metadata'
 * @param {boolean} [props.muted] - Should the video play muted
 * @param {boolean} [props.alwaysMuted] - disable unmuting
 * @param {string} [props.title] - Video title
 * @param {Object|Array} [props.widths] - Responsive widths for responsive poster image. If provided, poster will use Image component for responsive loading.
 */
const DirectVideo = forwardRef(
    (
        {
            sources,
            poster,
            autoplay = false,
            videoControls = false,
            loading = 'lazy',
            alwaysMuted,
            muted,
            title,
            objectFit = 'contain',
            widths,
            loop
        },
        ref
    ) => {
        const videoRef = useRef(null)
        const posterRef = useRef(null)
        const sys = useChakraContext()
        const shouldAutoplay = useVideoAutoplay(autoplay)
        const shouldLoop = loop || shouldAutoplay
        const {sharedLibraryStatic} = useOcapiSite()
        const assetBaseUrl = sharedLibraryStatic
        const nonce = useNonce()

        // Process sources with breakpoint-based media queries and type detection
        const processedSources = useMemo(() => {
            if (!sources || !Array.isArray(sources) || sources.length === 0) {
                return []
            }

            const breakpointValues = sys.breakpoints.values || []
            const breakpointMap = Object.fromEntries(
                breakpointValues.map((bp) => [bp.name, bp.min])
            )

            return sources
                .filter(hasValidVideoSource)
                .map((source) => {
                    const processed = {...source}

                    // Resolve src relative to assetBaseUrl for non-absolute URLs
                    const resolvedSrc = getSharedLibraryAssetUrl(processed.src, assetBaseUrl)

                    // Filter out if URL couldn't be resolved (relative URL without base)
                    if (!resolvedSrc) {
                        return null
                    }

                    processed.src = resolvedSrc

                    // Auto-detect type if not provided
                    if (!processed.type) {
                        processed.type = detectVideoType(processed.src)
                    }

                    // Convert breakpoint to media query if breakpoint is specified
                    // Remove breakpoint for base/all (applies to all sizes)
                    if (processed.breakpoint === 'base' || processed.breakpoint === 'all') {
                        delete processed.breakpoint
                    } else if (processed.breakpoint) {
                        const minWidth = breakpointMap[processed.breakpoint]
                        if (minWidth) {
                            processed.media = `(min-width: ${minWidth})`
                        }
                    }

                    return processed
                })
                .filter(Boolean)
                .sort((a, b) => {
                    // Primary sort: by breakpoint size (larger first) for proper source ordering
                    // Per MDN: browser selects first matching source, so larger breakpoints must come first
                    // Sources without breakpoint (including base/all) come last as fallback
                    const aHasBreakpoint = !!a.breakpoint
                    const bHasBreakpoint = !!b.breakpoint

                    if (!aHasBreakpoint && !bHasBreakpoint) {
                        // Both have no breakpoint, sort by compression priority
                        return getCompressionPriority(a.type) - getCompressionPriority(b.type)
                    }
                    if (!aHasBreakpoint) return 1 // No breakpoint = fallback, goes last
                    if (!bHasBreakpoint) return -1 // No breakpoint = fallback, goes last

                    // Both have breakpoints - sort larger first
                    const aMin = breakpointMap[a.breakpoint] || '0px'
                    const bMin = breakpointMap[b.breakpoint] || '0px'
                    const breakpointDiff = parseInt(bMin) - parseInt(aMin) // Reversed: larger first

                    // If same breakpoint, sort by compression priority
                    if (breakpointDiff === 0) {
                        return getCompressionPriority(a.type) - getCompressionPriority(b.type)
                    }

                    return breakpointDiff
                })
        }, [sources, sys.breakpoints.values, assetBaseUrl])

        // Expose play/pause methods to parent
        useImperativeHandle(ref, () => ({
            play: (muteVideo) => {
                if (muteVideo && !videoRef.current?.muted) {
                    videoRef.current.muted = true
                }

                videoRef.current?.play()
            },
            pause: () => {
                videoRef.current?.pause()
            }
        }))

        // prevent unmuting when alwaysMuted is true
        useEffect(() => {
            if (!alwaysMuted) return
            if (!videoRef.current) return

            const videoEl = videoRef.current

            const onVolumeChange = () => {
                if (!videoEl.muted) videoEl.muted = true
            }

            videoEl.muted = true
            videoEl.addEventListener('volumechange', onVolumeChange)
            return () => {
                videoEl.removeEventListener('volumechange', onVolumeChange)
            }
        }, [alwaysMuted])

        // Hide responsive poster when video starts playing
        useEffect(() => {
            if (!widths || !poster?.src) return
            if (!videoRef.current || !posterRef.current) return

            const videoEl = videoRef.current
            const posterEl = posterRef.current
            const container = posterEl.parentElement

            // Clean up SSR script handler if it exists
            if (container?._posterRemover) {
                container._posterRemover.video.removeEventListener(
                    'play',
                    container._posterRemover.handler
                )
                container._posterRemover.video.removeEventListener(
                    'playing',
                    container._posterRemover.handler
                )
                delete container._posterRemover
            }

            const hidePoster = () => {
                posterEl.style.display = 'none'
            }

            videoEl.addEventListener('playing', hidePoster)

            return () => {
                videoEl.removeEventListener('playing', hidePoster)
            }
        }, [widths, poster?.src])

        if (processedSources.length === 0) {
            console.warn('DirectVideo: sources is required and must be a non-empty array')
            return null
        }

        const videoProps = {
            ref: videoRef,
            style: {width: '100%', height: '100%', objectFit},
            title,
            muted: muted || alwaysMuted,
            playsInline: true
        }

        // Only use native poster attribute if widths is not provided
        if (!widths && poster?.src) {
            videoProps.poster = poster.src
        }

        if (shouldAutoplay) {
            videoProps.autoPlay = true
            videoProps.muted = true
        }

        if (shouldLoop) {
            videoProps.loop = true
        }

        if (videoControls) {
            videoProps.controls = true
        }

        if (loading === 'eager') {
            videoProps.preload = 'auto'
        } else {
            videoProps.preload = 'metadata'
        }

        const videoElement = (
            /* eslint-disable-next-line jsx-a11y/media-has-caption */
            <video data-testid="direct-video" aria-label={videoProps.title} {...videoProps}>
                {processedSources?.map((source, index) => {
                    const {breakpoint, ...sourceProps} = source
                    return (
                        <source
                            key={index}
                            data-testid={`video-source-${index}`}
                            {...sourceProps}
                        />
                    )
                })}
            </video>
        )

        // If widths is provided and poster exists, render responsive poster as overlay
        if (widths && poster?.src) {
            return (
                <>
                    <Box position="relative" width="100%" height="100%">
                        {videoElement}
                        <Box
                            ref={posterRef}
                            data-testid="direct-video-poster"
                            data-video-poster
                            position="absolute"
                            top="0"
                            left="0"
                            width="100%"
                            height="100%"
                            pointerEvents="none"
                        >
                            <Image
                                src={poster.src}
                                alt={poster.alt}
                                title={poster.title}
                                widths={widths}
                                loading={loading}
                                objectFit={objectFit}
                                fetchpriority={loading === 'eager' ? 'high' : 'low'}
                            />
                        </Box>
                    </Box>
                    {
                        // DO NOT INCLUDE THIS IN TESTS as it crashes istanbul coverage
                        !IS_TEST && (
                            // inline script to hide poster when video plays
                            // only used for SSR until hydration
                            <script
                                lang="javascript"
                                type="text/javascript"
                                nonce={nonce}
                                dangerouslySetInnerHTML={{
                                    __html: `(${preHydrationPosterRemover.toString()})()`
                                }}
                            />
                        )
                    }
                </>
            )
        }

        return videoElement
    }
)

if (process.env.NODE_ENV === 'development') {
    DirectVideo.displayName = 'DirectVideo'
}

DirectVideo.propTypes = {
    sources: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            type: PropTypes.string,
            breakpoint: PropTypes.string // Any breakpoint key from theme (xxs, xs, s, m, l, base, all, etc.)
        })
    ),
    poster: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
        title: PropTypes.string
    }),
    autoplay: PropTypes.bool,
    loop: PropTypes.bool,
    videoControls: PropTypes.bool,
    loading: PropTypes.oneOf(['eager', 'lazy']),
    title: PropTypes.string,
    alwaysMuted: PropTypes.bool,
    muted: PropTypes.bool,
    objectFit: PropTypes.string,
    widths: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
}

export default DirectVideo
